import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getProject, getProjects, consumeBoost } from '../api/projects';
import ProjectCard from '../components/ProjectCard';
import t from '../i18n/fr.json';
import '../styles/ProjectDetail.css';
import { useAuth } from '../context/AuthContext';
import { getYouTubeEmbedUrl } from '../constants/categories';
import Navbar from '../components/Navbar';
import banniere from '../images/helpadeal_banner_onlineico_1000x130.png';

const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

export default function ProjectDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const canGoBack = location.key !== 'default';
    const [project, setProject]   = useState({});
    const [promoted, setPromoted] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [voted, setVoted]           = useState(false);
    const [voteMsg, setVoteMsg]       = useState('');
    const [openFaq, setOpenFaq]       = useState(null);
    const { user, logoutUser }        = useAuth();
    const [boostRemaining, setBoostRemaining] = useState(0);
    const [boostTotal, setBoostTotal]         = useState(0);
    const [boosting, setBoosting]             = useState(false);

    const [banniereLoaded, setBanniereLoaded] = useState(false);

    useEffect(() => {
    async function fetchData() {
        setLoading(true);
        const proj = await getProject(slug);
        setProject(proj);
        const confirmed = parseInt(proj.boost_confirmed) || 0;
        const consumed  = parseInt(proj.boosts) || 0;
        setBoostTotal(confirmed);
        setBoostRemaining(confirmed - consumed);

            ////////////////////////////
            // Projets avec banner active (panneau droit)
            ////////////////////////////
            const voteCheck = await fetch(`/api/vote.php?project_id=${proj.id}`);
            const voteData  = await voteCheck.json();
            if (voteData.already_voted) setVoted(true);

            if (proj.promoted) {
                setPromoted(proj.promoted);
            } else {
                setPromoted([]);
            }
            setLoading(false);
        }
        fetchData();
    }, [slug]);


    const handleVote = async () => {
        if (voted) return;
        const token = localStorage.getItem('onlineico_token');
        const res = await fetch(`/api/vote.php?project_id=${project.id}`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        const data = await res.json();
        if (data.success) {
            setVoted(true);
            setProject(prev => ({ ...prev, total_votes: data.total_votes }));
            setVoteMsg('✓ Vote registered! Come back in 24h.');
            setTimeout(() => setVoteMsg(''), 4000);
        } else {
            setVoteMsg(data.error || 'Error');
            setTimeout(() => setVoteMsg(''), 4000);
        }
    };

    if (loading || !project || !project.name) return <div className="app-wrap"><div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div></div>;

    const colors    = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];
    const color     = colors[project.id % colors.length];
    const tokenomics = project.tokenomics ? JSON.parse(project.tokenomics) : null;
    const roadmap    = project.roadmap ? JSON.parse(project.roadmap) : null;
    const team       = project.team ? JSON.parse(project.team) : null;
    const tokenDist  = project.token_distribution ? JSON.parse(project.token_distribution) : null;
    const faq        = project.faq ? JSON.parse(project.faq) : null;

    ////////////////////////////
    // Projets premium (section À la une)
    ////////////////////////////
    const premiumProjects = project.premium_projects || [];

    const metaDesc = project.description
        ? project.description.replace(/<[^>]+>/g, '').slice(0, 155) + '…'
        : `Discover ${project.name || ''} — ICO / IDO presale on OnlineICO.`;

    return (
        <div className="app-wrap">

<Helmet>
    {/* Title */}
    <title>{project.name ? `${project.name} (${project.ticker || ''}) — ${project.type?.toUpperCase() || 'ICO'} | OnlineICO` : 'Project | OnlineICO'}</title>
    {/* Meta description */}
    <meta name="description" content={metaDesc} />
    <meta name="keywords" content="ICO crypto, crypto presale, token sale, ICO presale, crypto investment, new crypto, upcoming ICO, token launch, crypto gem, early stage crypto, whitelist crypto, crypto rewards, help platform, crypto protection, Web3, decentralized, smart contract, ERC-20, BEP-20, Solana, Ethereum, BNB Chain, tokenomics, crypto listing, OnlineICO"/>
    {/* Canonical */}
    <link rel="canonical" href={`https://onlineico.com/${project.type || 'ico'}/${slug}`} />

    {/* Open Graph */}
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="OnlineICO" />
    <meta property="og:title" content={project.name ? `${project.name} — ${project.type?.toUpperCase() || 'ICO'} | OnlineICO` : 'Project | OnlineICO'} />
    <meta property="og:description" content={metaDesc} />
    <meta property="og:url" content={`https://onlineico.com/${project.type || 'ico'}/${slug}`} />
    <meta property="og:image" content={project.logo_url || 'https://onlineico.com/og-image.png'} />
    <meta property="og:image:width" content="512" />
    <meta property="og:image:height" content="512" />

    {/* Twitter Cards */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@online_ico" />
    <meta name="twitter:title" content={project.name ? `${project.name} (${project.ticker || ''}) — OnlineICO` : 'Project | OnlineICO'} />
    <meta name="twitter:description" content={metaDesc} />
    <meta name="twitter:image" content={project.logo_url || 'https://onlineico.com/og-image.png'} />

    {/* Schema.org JSON-LD */}
    <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "InvestmentFund",
        "additionalType": project.type?.toUpperCase(),
        "name": project.name,
        "description": project.description?.substring(0, 300),
        "image": project.logo_url || 'https://onlineico.com/og-image.png',
        "url": `https://onlineico.com/${project.type || 'ico'}/${slug}`,
        "identifier": project.ticker,
        "category": project.type?.toUpperCase() || 'ICO',
        "brand": {
            "@type": "Brand",
            "name": project.name
        },
        "offers": (() => {
            try {
                const tok = project.tokenomics ? JSON.parse(project.tokenomics) : null;
                if (tok?.phases?.length > 0) {
                    return {
                        "@type": "Offer",
                        "price": tok.phases[0].price,
                        "priceCurrency": tok.devise || "USDT"
                    };
                }
                return undefined;
            } catch { return undefined; }
        })()
    })}</script>
</Helmet>

          <Navbar />



{/*   BANNIERE */}
<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
  {!banniereLoaded && (
    <div style={{
      width: '100%',
      borderRadius: '12px',
      background: 'rgba(255,255,255,0.06)',
      animation: 'shimmer 1.5s infinite'
    }} />
  )}

<a href='/ico/HELPADEAL'>
  <img
    src={banniere}
    alt="HelpaDeal - Collaborative Security"
    title="HelpaDeal - Collaborative Security"
    onLoad={() => setBanniereLoaded(true)}
    style={{ width: '100%', objectFit: 'cover', display: banniereLoaded ? 'block' : 'none', marginTop:'0px', }}
  /></a>
</div>



            {/* SECTION À LA UNE — projets premium */}
            {premiumProjects.length > 0 && (
                <div style={{ maxWidth: '1100px', margin: '0 auto 24px', padding: '0 1.5rem', marginTop: '20px' }}>
                    <div className="featured-label">★ {t.featured_label}</div>
                    <div className="featured-cards">
                        {premiumProjects.map(p => (
                            <ProjectCard key={p.id} project={p} />
                        ))}
                    </div>
                </div>
            )}

            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>
                <button className="back-btn" onClick={() => canGoBack ? navigate(-1) : navigate('/listings')}>← Back to listings</button>
            </div>

            <div className="detail-wrap">

                {/* COLONNE GAUCHE */}
                <div className="detail-left">

                    {/* HEADER */}
                    <div className="detail-header">
                        <div className="detail-header-top">
                            <div className="detail-logo" style={{ background: project.logo_url ? 'transparent' : color }}>
                                {project.logo_url
                                    ? <img src={project.logo_url} alt={project.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} />
                                    : project.name.substring(0, 2).toUpperCase()
                                }
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="detail-name">{project.name}</div>
                                <div className="detail-ticker">{project.ticker}</div>
                                <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.07)', margin: '10px 0' }} />
                                <div className="detail-badges">
                                    <span className={`status-badge status-${project.status}`}>
                                        {project.status === 'live' ? t.live : project.status === 'upcoming' ? t.upcoming : t.ended}
                                    </span>
                                    <span className="chain-badge">{project.chain}</span>
                                    {project.categorie && (
                                        <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: 'rgba(139,92,246,0.12)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.3)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            ✦ {project.categorie}
                                        </span>
                                    )}
                                    {project.kyc && (
                                        <a href={project.kyc_url || '#'} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.68rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.3)', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
                                            ✅ KYC · {project.kyc}
                                        </a>
                                    )}
                                    {project.audit && (
                                        <a href={project.audit_url || '#'} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.68rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.3)', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
                                            ✅ Audit · {project.audit}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ADRESSE DU CONTRAT */}
                        {project.contract_address && (
                            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '10px 16px' }}>
                                <span style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', flexShrink: 0 }}>Contract</span>
                                <span style={{ color: '#E2E8F0', fontSize: '0.82rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>{project.contract_address}</span>
                            </div>
                        )}

                    </div>

                    {/* PRESALE + RÉSEAUX SOCIAUX */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch', flexWrap: 'wrap' }}>

                        {/* PRESALE */}
                        {(project.presale_start || project.presale_end) && (
                            <div className="detail-section" style={{ flex: 1, margin: 0 }}>
                                <div className="detail-section-title"><span>🗓️</span> Presale</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                    {project.presale_start && (
                                        <div>
                                            <div style={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Start</div>
                                            <div style={{ color: '#34D399', fontWeight: '600', fontSize: '0.9rem' }}>{formatDate(project.presale_start)}</div>
                                        </div>
                                    )}
                                    {project.presale_start && project.presale_end && (
                                        <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.07)' }} />
                                    )}
                                    {project.presale_end && (
                                        <div>
                                            <div style={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>End</div>
                                            <div style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.9rem' }}>{formatDate(project.presale_end)}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* RÉSEAUX SOCIAUX */}
                        {(project.website || project.twitter || project.telegram || project.whitepaper) && (
                            <div className="detail-section" style={{ flex: 1, margin: 0 }}>
                                <div className="detail-section-title"><span>🔗</span> Social links</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {project.website    && <a href={project.website}    target="_blank" rel="noopener noreferrer" className="social-btn website">🌐 Website</a>}
                                    {project.twitter    && <a href={project.twitter}    target="_blank" rel="noopener noreferrer" className="social-btn twitter">𝕏 Twitter</a>}
                                    {project.telegram   && <a href={project.telegram}   target="_blank" rel="noopener noreferrer" className="social-btn telegram">✈ Telegram</a>}
                                    {project.whitepaper && <a href={project.whitepaper} target="_blank" rel="noopener noreferrer" className="social-btn whitepaper">📄 Whitepaper</a>}
                                </div>
                            </div>
                        )}

                    </div>

                    {/* DESCRIPTION */}
                    <div className="detail-section" style={{ marginTop: '24px' }}>
                        <div className="detail-section-title"><span>📝</span> Description</div>
                        <p className="detail-desc">{project.description}</p>
                    </div>

                    {/* VIDÉO */}
                    {project.url_video && getYouTubeEmbedUrl(project.url_video) && (
                        <div className="detail-section">
                            <div className="detail-section-title"><span>🎬</span> Video</div>
                            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}>
                                <iframe
                                    src={getYouTubeEmbedUrl(project.url_video)}
                                    title={`Video ${project.name}`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* TOKENOMICS */}
                    {tokenomics && (
                        <div className="detail-section">
                            <div className="detail-section-title"><span>📊</span> Tokenomics</div>
                            {/* Champs sans vesting */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                                {Object.entries(tokenomics).filter(([k]) => k !== 'phases' && k !== 'Vesting').map(([key, val]) => (
                                    <div key={key} style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.15)', borderRadius: '16px', padding: '16px', textAlign: 'center' }}>
                                        <div style={{ color: '#34D399', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.2rem', marginBottom: '4px' }}>{val}</div>
                                        <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{key}</div>
                                    </div>
                                ))}
                            </div>
                            {tokenDist && (
                                <>
                                    <div style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Distribution</div>
                                    <div style={{ height: '36px', borderRadius: '12px', overflow: 'hidden', display: 'flex', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        {tokenDist.map((item, i) => (
                                            <div key={i} style={{ width: `${item.percent}%`, background: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: '700', color: '#0B1120' }}>
                                                {item.percent}%
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '6px', marginTop: '16px' }}>
                                        {tokenDist.map((item, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: item.color, flexShrink: 0 }} />
                                                <span style={{ color: '#E2E8F0', fontSize: '0.82rem' }}>{item.label} <span style={{ color: item.color, fontWeight: '600' }}>{item.percent}%</span></span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {tokenomics.phases && (
                                <div style={{ marginTop: '20px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                                        {tokenomics.phases.map((phase, i) => (
                                            <div key={i} style={{ textAlign: 'center', padding: '20px 12px', borderRadius: '16px', background: `rgba(74,222,128,${0.05 + i * 0.05})`, border: '1px solid rgba(74,222,128,0.2)', transition: 'all 0.3s ease' }}
                                                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 15px 30px -10px rgba(74,222,128,0.3)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
                                                <div style={{ marginBottom: '10px' }}>
                                                    <span style={{ background: 'rgba(0,0,0,0.4)', color: '#F59E0B', padding: '4px 14px', borderRadius: '30px', fontSize: '0.82rem', fontWeight: '600', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                        ✦ Phase {i + 1} ✦
                                                    </span>
                                                </div>
                                                <div style={{ background: 'linear-gradient(135deg, #4ADE80, #22C55E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.3rem' }}>
                                                    ${phase.price} <span style={{ color: '#94a3b8', fontSize: '0.8rem', WebkitTextFillColor: '#94a3b8' }}>USDT</span>
                                                </div>
                                                {phase.duration && <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '6px' }}>{phase.duration}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Vesting — affiché en dernier, séparé par virgules */}
                            {tokenomics['Vesting'] && (() => {
                                const vestingColors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];
                                const items = tokenomics['Vesting'].split(',').map(s => s.trim()).filter(Boolean);
                                return (
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ marginBottom: '12px', marginTop: '24px', fontSize: '0.85rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Vesting Schedule</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(140px, 1fr))`, gap: '12px' }}>
                                            {items.map((item, i) => {
                                                const color = vestingColors[i % vestingColors.length];
                                                return (
                                                    <div key={i} style={{ background: `${color}15`, border: `1px solid ${color}40`, borderRadius: '16px', padding: '16px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <div style={{ color, fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '0.88rem', lineHeight: '1.5' }}>{item}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* ROADMAP */}
                    {roadmap && (
                        <div className="detail-section">
                            <div className="detail-section-title"><span>🗺️</span> Roadmap</div>
                            <div className="roadmap-list">
                                {roadmap.map((item, i) => (
                                    <div className="roadmap-item" key={i}>
                                        <div className="roadmap-dot" style={{ background: i === 0 ? '#34D399' : i === 1 ? '#3B82F6' : i === 2 ? '#F59E0B' : '#8B5CF6' }} />
                                        <div className="roadmap-text">{item}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TEAM */}
                    {team && (
                        <div className="detail-section">
                            <div className="detail-section-title"><span>👥</span> Team</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                {team.map((member, i) => {
                                    const memberColors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899'];
                                    const mc = memberColors[i % memberColors.length];
                                    return (
                                        <div key={i} style={{ background: 'rgba(20,30,50,0.5)', borderRadius: '20px', padding: '20px', textAlign: 'center', border: `1px solid ${mc}30`, transition: 'transform 0.3s ease' }}
                                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                                            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `linear-gradient(135deg, ${mc}, ${memberColors[(i + 1) % memberColors.length]})`, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '1.2rem', color: '#0B1120' }}>
                                                {member.name.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.95rem', marginBottom: '4px' }}>{member.name}</div>
                                            <div style={{ color: mc, fontSize: '0.82rem', fontWeight: '600', marginBottom: '10px' }}>{member.role}</div>
                                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: '1.5' }}>{member.bio}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* FAQ */}
                    {faq && (
                        <div className="detail-section">
                            <div className="detail-section-title"><span>❓</span> FAQ</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {faq.map((item, i) => (
                                    <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', cursor: 'pointer', color: '#F1F5F9', fontSize: '0.95rem', fontWeight: '500' }}
                                            onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                            <span>{item.q}</span>
                                            <span style={{ color: '#34D399', fontSize: '1.2rem', transition: 'transform 0.3s', transform: openFaq === i ? 'rotate(90deg)' : 'rotate(-90deg)' }}>➔</span>
                                        </div>
                                        {openFaq === i && (
                                            <div style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', borderLeft: '3px solid #3B82F6', color: '#93c5fd', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '8px' }}>
                                                {item.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* COLONNE DROITE */}
                <div className="detail-right">
                    {/* BOUTON BOOST — propriétaire uniquement */}
                        {user && project.user_id && parseInt(user.id) === parseInt(project.user_id) && (
                            <div style={{ background: 'rgba(10,15,30,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '24px', padding: '20px', marginBottom: '16px' }}>
                                <button
                                    disabled={boosting}
                                    style={{ width: '100%', padding: '10px', borderRadius: '30px', border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#F59E0B', fontWeight: '700', fontSize: '0.9rem', cursor: boosting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif', opacity: boosting ? 0.7 : 1 }}
                                    onClick={async () => {
                                        if (boostRemaining > 0) {
                                            setBoosting(true);
                                            const res = await consumeBoost(project.slug);
                                            if (res.success) {
                                                setBoostRemaining(res.remaining);
                                                setProject(prev => ({ ...prev, boosts: res.boost_consumed }));
                                            }
                                            setBoosting(false);
                                        } else {
                                            navigate(`/boost/${project.slug}`);
                                        }
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,158,11,0.15)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(245,158,11,0.08)'}
                                >
                                    {boosting ? '⏳ Boosting...' : boostRemaining > 0 ? '⚡ Boost this project' : '⚡ Buy boosts'}
                                </button>
                                {boostTotal > 0 && (
                                    <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '0.78rem', color: boostRemaining > 0 ? '#F59E0B' : '#64748b' }}>
                                        {boostRemaining > 0
                                            ? `${boostTotal - boostRemaining}/${boostTotal} boosts used`
                                            : `${boostTotal}/${boostTotal} — stock depleted, recharge from My Account`}
                                    </div>
                                )}
                            </div>
                        )}

                    {/* BOUTON VOTE */}
                    <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                            <span style={{ color: '#F59E0B', fontSize: '0.9rem', fontWeight: '600' }}>{parseInt(project.total_votes).toLocaleString()}</span>
                            <button
                                className={`vote-panel-btn${voted ? ' voted' : ''}`}
                                onClick={handleVote}
                            >
                                {voted ? t.voted_btn : `▲ ${t.vote_btn}`}
                            </button>
                        </div>
                        {voteMsg && (
                            <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '0.75rem', color: voteMsg.startsWith('✓') ? '#34D399' : '#f87171' }}>
                                {voteMsg}
                            </div>
                        )}
                    </div>

                    {/* EARN — quests disponibles */}
                    {project.has_quests && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '12px 0' }} />
                            <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', textAlign: 'center' }}>
                                <div style={{ color: '#34D399', fontSize: '0.85rem', fontWeight: '800', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '12px' }}>🎯 Earn</div>
                                <a
                                    href={`/earn/${project.slug}`}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 18px', borderRadius: '8px', background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.3)', fontSize: '0.82rem', fontWeight: '600', textDecoration: 'none' }}
                                >
                                    View quests →
                                </a>
                            </div>
                        </div>
                    )}

                    {/* PANNEAU BANNER — projets avec bannière active */}
                    {promoted.length > 0 && (
                        <div className="promoted-panel" style={{ marginTop: '16px' }}>
                            <div className="promoted-panel-title">★ DISCOVER</div>
                            {promoted.map(p => {
                                const firstPrice = p.tokenomics ? (() => {
                                    try {
                                        const tok = JSON.parse(p.tokenomics);
                                        return tok.phases && tok.phases.length > 0 ? tok.phases[0].price : null;
                                    } catch { return null; }
                                })() : null;

                                return (
                                    <div key={p.id} className="promoted-mini-card" onClick={() => navigate(`/${p.type}/${p.slug}`)}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            {p.logo_url ? (
                                                <img src={p.logo_url} alt={p.name} style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                                            ) : (
                                                <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: colors[p.id % colors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', color: '#0B1120', flexShrink: 0 }}>
                                                    {p.name.substring(0, 2).toUpperCase()}
                                                    
                                                </div>
                                            )}
                                            <div className="promoted-mini-name promoted-badge" style={{fontSize:'0.6rem'}}>{p.name}</div>
                                        </div>
                                        
                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '8px 0' }} />
                                        {p.description && (
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: '1.5', marginBottom: '8px' }}>
                                                {p.description.substring(0, 140)}{p.description.length > 140 ? '...' : ''}
                                            </div>
                                        )}
                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '8px 0' }} />
                                        {firstPrice && (
                                            <div style={{ fontSize: '0.78rem', color: '#34D399', fontWeight: '600', marginBottom: '6px' }}>
                                                Price: ${firstPrice}
                                            </div>
                                        )}
                                        {(p.presale_start || p.presale_end) && (() => {
                                            const now   = Date.now();
                                            const start = p.presale_start ? new Date(p.presale_start).getTime() : null;
                                            const end   = p.presale_end   ? new Date(p.presale_end).getTime()   : null;
                                            let progress = 0;
                                            if (start && end && now >= start) {
                                                progress = Math.min(100, Math.round(((now - start) / (end - start)) * 100));
                                            }
                                            return (
                                                <div style={{ marginTop: '10px' }}>
                                                    {p.presale_start && (
                                                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '5px' }}>
                                                            Start : <span style={{ color: 'rgba(245,158,11,0.8)' }}>{formatDate(p.presale_start)}</span>
                                                        </div>
                                                    )}
                                                    {start && end && (
                                                        <div>
                                                            <div
                                                                title={`End : ${p.presale_end}`}
                                                                style={{ position: 'relative', height: '5px', borderRadius: '10px', background: 'rgba(255,255,255,0.07)', cursor: 'pointer', overflow: 'hidden' }}
                                                            >
                                                                <div style={{
                                                                    width: `${progress}%`,
                                                                    height: '100%',
                                                                    borderRadius: '10px',
                                                                    background: progress >= 100 ? '#f87171' : 'linear-gradient(90deg, #34D399, #F59E0B)',
                                                                    transition: 'width 0.4s ease'
                                                                }} />
                                                            </div>
                                                            <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '0.65rem', color: progress >= 100 ? '#f87171' : '#64748b' }}>
                                                                {progress}%
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
